/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-04-23 12:01:29
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-09-18 10:07:59
 */

/**
 * ViewerHeader 组件
 *
 * 功能：显示照片查看器的顶部导航栏，包含作者信息和关闭按钮
 * 显示当前模式状态（普通/对比模式），并提供返回/关闭功能
 */

import React, { useState, useEffect } from 'react'

import cheat_icon from '../../../../images/cheat_icon.png'
import cheat_icon2 from '../../../../images/cheat_icon2.png'
import {
  getAbnormalPhotoTagInfo,
  getSensorContent
} from '../../../../utils/photoUtils'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import { CloseCircleOutlined, CloseOutlined } from '@ant-design/icons'

// 类型断言辅助函数，用于处理 photoUtils.js 未声明类型的问题
const getTagInfo = getAbnormalPhotoTagInfo as (
  sourceType: number | undefined
) => { type: string; text: string } | null
const getContent = getSensorContent as (
  sourceType: number | undefined
) => string

interface Member {
  headimgurl?: string
  nickname?: string
}

interface Photo {
  sourceType?: number
  photoRectification?: {
    exist?: boolean
    recordID?: string
  }
  seq?: string
  userID?: string
  time?: number
}

interface ViewerHeaderProps {
  member: Member | null | undefined
  photo: Photo | null
  isComparisonMode: boolean
  aiProcessing: boolean
  onClose: () => void
  onViewByPerson: (member: any) => void
  handleReportSA: (clickItem: string, ...args: any[]) => void
  groupId?: string
  myRole?: string | number
}

// 合并后的异常照片组件 - 包含图标和提示框
const AbnormalPhotoComponent = ({
  photo,
  visible,
  onClose,
  onShow
}: {
  photo: Photo | null
  visible: boolean
  onClose: () => void
  onShow: () => void
}) => {
  const sourceType = photo?.sourceType
  const alertInfo = getTagInfo(sourceType)

  if (!alertInfo) return null

  return (
    <div className="abnormal-photo-container">
      {/* 异常照片图标 - 始终显示 */}
      <div className="abnormal-photo-icon" onClick={onShow}>
        <img
          src={alertInfo.type === 'fake' ? cheat_icon2 : cheat_icon}
          alt=""
        />
      </div>

      {/* 异常照片提示框 - 根据visible状态显示/隐藏 */}
      {visible && (
        <div
          className={`abnormal-photo-tooltip abnormal-photo-tooltip-${alertInfo.type}`}
        >
          <div className="tooltip-content">
            <span className="tooltip-text">{alertInfo.text}</span>
            <CloseOutlined className="tooltip-close" onClick={onClose} />
          </div>
        </div>
      )}
    </div>
  )
}

const ViewerHeader: React.FC<ViewerHeaderProps> = ({
  member,
  photo,
  isComparisonMode,
  aiProcessing,
  onClose,
  onViewByPerson,
  handleReportSA,
  groupId,
  myRole
}) => {
  // 异常照片提示状态管理
  const [abnormalAlertVisible, setAbnormalAlertVisible] = useState(true)

  // 检查是否有整改记录
  const photoRectification = photo?.photoRectification
  const hasRectification = photoRectification?.exist
  const rectificationId = photoRectification?.recordID

  // 检查是否为异常照片
  const isPhotoAbnormal = getTagInfo(photo?.sourceType) !== null

  // 处理异常照片曝光埋点的方法
  const handleAbnormalPhotoExposure = () => {
    if (isPhotoAbnormal && !isComparisonMode) {
      sensors.track('workgroup_b4312_c7642_exposure', {
        string1: photo?.seq,
        string2: photo?.userID,
        number1: photo?.time,
        popType: getContent(photo?.sourceType),
        role: sensorsRole(myRole),
        groupID: groupId || null,
        track_sign:
          'aworkgroup.b4312.c7642.exposure.workgroup_b4312_c7642_exposure',
        requirement_id: 276
      })
    }
  }

  // 切换照片时保持异常提示状态并上报曝光埋点
  useEffect(() => {
    // 如果有异常照片且不在对比模式，上报曝光埋点
    handleAbnormalPhotoExposure()
  }, [photo, isComparisonMode])

  // 处理异常照片埋点的方法
  const handleAbnormalIconClick = (action: string) => {
    const clickItem = action
    sensors.track('workgroup_b4312_c7642_click', {
      clickItem,
      string1: photo?.seq,
      string2: photo?.userID,
      number1: photo?.time,
      popType: getContent(photo?.sourceType),
      role: sensorsRole(myRole),
      groupID: groupId || null,
      track_sign: 'aworkgroup.b4312.c7642.click.workgroup_b4312_c7642_click',
      requirement_id: 276
    })
  }

  const handleRectificationClick = () => {
    if (hasRectification && rectificationId) {
      handleReportSA('照片已发起整改去查看')

      // 跳转到整改详情页面，携带整改ID
      window.open(`/rectify/list?recordID=${rectificationId}`, '_blank')
    }
  }

  return (
    <div
      className="headerBackGround"
      style={{ display: aiProcessing ? 'none' : 'flex' }}
    >
      {!isComparisonMode && (
        <div
          className="authorBox"
          onClick={() => {
            if (member) {
              onViewByPerson(member)
            }
          }}
        >
          <img src={member?.headimgurl} alt="" />
          <span>{member?.nickname}</span>
        </div>
      )}

      {/* 整改入口 */}
      {hasRectification && !isComparisonMode && (
        <div className="rectification-entry" onClick={handleRectificationClick}>
          <span>照片已发起整改，去查看 &gt;</span>
        </div>
      )}

      {/* 异常照片组件 - 合并后的单一元素 */}
      {isPhotoAbnormal && !isComparisonMode && (
        <AbnormalPhotoComponent
          photo={photo}
          visible={abnormalAlertVisible}
          onClose={() => {
            handleAbnormalIconClick('关闭')
            setAbnormalAlertVisible(false)
          }}
          onShow={() => {
            handleAbnormalIconClick('开启')
            setAbnormalAlertVisible(true)
          }}
        />
      )}

      <div className="header" onClick={onClose}>
        <CloseCircleOutlined className="close" />
        <span>{isComparisonMode ? '退出变清晰' : '关闭'}</span>
      </div>
    </div>
  )
}

export default ViewerHeader
